import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  VStack,
  Select,
  Text,
  Badge,
  FormControl,
  FormLabel,
  Divider,
  Icon,
} from '@chakra-ui/react';
import { ChevronRightIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { useUserLists } from './useUserLists';
import { expandList } from './expandList';
import { SHARE_ROLES } from '../utils/shareAccess';

const ROLE_OPTIONS = [
  { value: SHARE_ROLES.ADMIN, label: 'Admin' },
  { value: SHARE_ROLES.WRITE, label: 'Editor' },
  { value: SHARE_ROLES.VIEW, label: 'Viewer' },
];

const MIXED = 'mixed';

/**
 * Apply a saved user list to the current share modal. Single source of truth
 * for the apply-time expansion, the role-pick UI, and the "M of N" hint.
 *
 * Collapsed by default so the host modal's existing per-user share flow stays
 * the obvious default. Click the header to expand and use a list. Lists are
 * an optional shortcut, never required.
 *
 * Selecting a list expands its members inline so the user can preview who
 * will be shared with and override individual roles before clicking Apply.
 * The list-level role dropdown displays "Mixed" when individual overrides
 * diverge from any uniform value.
 *
 * Props:
 *   contextUsers   Array<{ id, ... }>     users available in this share context
 *   excludeUserIds Set<string>             users to skip (current, owner, already shared)
 *   onApply        (expanded) => void      expanded: [{ user, role, source: {listId, listName} }]
 *   onOpenManager  () => void              opens <UserListManagerModal>
 *   defaultRole    string                  default role for the role dropdown
 *   defaultExpanded boolean                start expanded (default false)
 */
export function UserListPicker({
  contextUsers,
  excludeUserIds,
  onApply,
  onOpenManager,
  defaultRole = SHARE_ROLES.ADMIN,
  defaultExpanded = false,
  applyButtonLabel = 'Apply',
}) {
  const { lists, getMembers } = useUserLists();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [selectedListId, setSelectedListId] = useState('');
  const [role, setRole] = useState(defaultRole);
  // roleByUserId is a plain object (id -> role). Per-user overrides set here
  // make computedListRole show "Mixed" until they're harmonized.
  const [roleByUserId, setRoleByUserId] = useState({});
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!selectedListId) {
      setMembers([]);
      setRoleByUserId({});
      return () => {};
    }
    setMembersLoading(true);
    getMembers(selectedListId)
      .then((ids) => {
        if (!cancelled) setMembers(ids);
      })
      .catch(() => {
        if (!cancelled) setMembers([]);
      })
      .finally(() => {
        if (!cancelled) setMembersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedListId, getMembers]);

  const selectedList = useMemo(
    () => lists.find((l) => l.id === selectedListId) || null,
    [lists, selectedListId],
  );

  const expansion = useMemo(() => {
    if (!selectedList) return null;
    return expandList({
      list: { id: selectedList.id, name: selectedList.name, members },
      contextUsers: contextUsers || [],
      excludeIds: excludeUserIds || new Set(),
    });
  }, [selectedList, members, contextUsers, excludeUserIds]);

  // When the expansion changes (new list selected or members loaded),
  // drop any per-user overrides for users no longer in the expansion.
  useEffect(() => {
    if (!expansion) return;
    setRoleByUserId((prev) => {
      const validIds = new Set(expansion.expanded.map((e) => String(e.user.id)));
      const next = {};
      for (const [id, r] of Object.entries(prev)) {
        if (validIds.has(id)) next[id] = r;
      }
      return next;
    });
  }, [expansion]);

  const roleForUser = (userId) =>
    roleByUserId[String(userId)] ?? role;

  const computedListRole = useMemo(() => {
    if (!expansion || expansion.expanded.length === 0) return role;
    const roles = expansion.expanded.map((e) => roleForUser(e.user.id));
    const first = roles[0];
    return roles.every((r) => r === first) ? first : MIXED;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expansion, roleByUserId, role]);

  const handleListRoleChange = (newRole) => {
    if (newRole === MIXED) return; // Display-only; no-op when chosen.
    setRole(newRole);
    if (expansion) {
      const next = {};
      for (const e of expansion.expanded) next[String(e.user.id)] = newRole;
      setRoleByUserId(next);
    }
  };

  const handleUserRoleChange = (userId, newRole) => {
    setRoleByUserId((prev) => ({ ...prev, [String(userId)]: newRole }));
  };

  const totalMembers = members.length;
  const inContext = expansion ? expansion.expanded.length : 0;
  const canApply = !!selectedList && !membersLoading && inContext > 0;

  const handleApply = () => {
    if (!expansion) return;
    onApply(
      expansion.expanded.map((e) => ({ ...e, role: roleForUser(e.user.id) })),
    );
    setSelectedListId('');
    setRoleByUserId({});
  };

  return (
    <Box
      borderWidth="1px"
      borderColor="gray.200"
      bg="gray.50"
      borderRadius="md"
      px={3}
      py={expanded ? 3 : 1.5}
      mb={3}
    >
      <HStack
        as="button"
        type="button"
        onClick={() => setExpanded((v) => !v)}
        spacing={2}
        w="100%"
        justifyContent="flex-start"
        aria-expanded={expanded}
      >
        <Icon
          as={expanded ? ChevronDownIcon : ChevronRightIcon}
          color="gray.500"
        />
        <Text fontSize="xs" fontWeight="semibold" color="gray.600" textTransform="uppercase">
          Apply a saved list (optional shortcut)
        </Text>
        <Badge colorScheme="blue">NEW</Badge>
      </HStack>
      {expanded && (
        <Box mt={2}>
          <HStack spacing={2} flexWrap="wrap">
            <FormControl flex="1" minW="200px">
              <FormLabel htmlFor="user-list-picker-list" srOnly>List</FormLabel>
              <Select
                id="user-list-picker-list"
                placeholder="— Pick a list —"
                value={selectedListId}
                onChange={(e) => setSelectedListId(e.target.value)}
              >
                {lists.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name} ({l.member_count})
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl w="140px">
              <FormLabel htmlFor="user-list-picker-role" srOnly>Role</FormLabel>
              <Select
                id="user-list-picker-role"
                value={computedListRole}
                onChange={(e) => handleListRoleChange(e.target.value)}
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
                <option value={MIXED}>Mixed</option>
              </Select>
            </FormControl>
            <Button colorScheme="blue" onClick={handleApply} isDisabled={!canApply}>
              {applyButtonLabel}
            </Button>
            <Button variant="outline" onClick={onOpenManager}>
              Manage lists
            </Button>
          </HStack>
          {selectedList && (
            <Text mt={2} fontSize="xs" color="gray.600">
              {membersLoading
                ? 'Loading members…'
                : `${inContext} of ${totalMembers} member${totalMembers === 1 ? '' : 's'} in this clinic` +
                  (expansion && expansion.skippedAlreadyShared
                    ? ` · ${expansion.skippedAlreadyShared} already shared`
                    : '')}
            </Text>
          )}
          {selectedList && !membersLoading && expansion && expansion.expanded.length > 0 && (
            <Box mt={3}>
              <Divider mb={2} />
              <Text fontSize="xs" fontWeight="semibold" color="gray.600" textTransform="uppercase" mb={2}>
                Members
              </Text>
              <VStack align="stretch" spacing={1}>
                {expansion.expanded.map((e) => {
                  const u = e.user;
                  const userRole = roleForUser(u.id);
                  return (
                    <HStack key={u.id} spacing={2}>
                      <Box flex="1" minW="0">
                        <Text fontSize="sm" noOfLines={1}>
                          {u.name || u.email || u.id}
                        </Text>
                        {u.email && u.name && (
                          <Text fontSize="xs" color="gray.500" noOfLines={1}>
                            {u.email}
                          </Text>
                        )}
                      </Box>
                      <Select
                        size="sm"
                        w="120px"
                        value={userRole}
                        aria-label={`Role for ${u.name || u.email || u.id}`}
                        onChange={(ev) => handleUserRoleChange(u.id, ev.target.value)}
                      >
                        {ROLE_OPTIONS.map((r) => (
                          <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                      </Select>
                    </HStack>
                  );
                })}
              </VStack>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
